import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Badge,
  Button,
  Input
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import Paginations from "../../components/Common/Pagination";
import Spinners from "../../components/Common/Spinner";
import { resolvePaginatedList } from "../../helpers/paginatedResponse";
import { useGetAllLeaveRequests, useLeaveRequestDecision } from "../../api/booking";
import {
  ADMIN_LEAVE_FILTER_ALL,
  LEAVE_STATUS,
  LEAVE_STATUS_LABELS,
  LEAVE_TYPE,
  LEAVE_TYPE_LABELS,
  SICKNESS_MARK_TYPE,
  SICKNESS_MARK_TYPE_LABELS
} from "../../constants/booking";
import LeaveRequestDetailModal from "./LeaveRequestDetailModal";
import toastr from "toastr";

const LeaveRequests = () => {
  document.title = "Leave Requests | Admin";

  const [detailRequest, setDetailRequest] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [typeFilter, setTypeFilter] = useState(ADMIN_LEAVE_FILTER_ALL);
  const [statusFilter, setStatusFilter] = useState(ADMIN_LEAVE_FILTER_ALL);
  const [sicknessMarkTypeFilter, setSicknessMarkTypeFilter] = useState(
    ADMIN_LEAVE_FILTER_ALL
  );

  const { data, isPending: isLoading, mutate: fetchRequests, error } = useGetAllLeaveRequests();
  const makeDecision = useLeaveRequestDecision();

  const buildFetchParams = useCallback(
    () => ({
      page,
      limit,
      search: search.trim() || undefined,
      type: typeFilter,
      status: statusFilter,
      sicknessMarkType: sicknessMarkTypeFilter
    }),
    [page, limit, search, typeFilter, statusFilter, sicknessMarkTypeFilter]
  );

  const lastFetchKeyRef = useRef("");
  const fetchKey = JSON.stringify(buildFetchParams());

  useEffect(() => {
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;
    fetchRequests(buildFetchParams());
  }, [fetchKey, fetchRequests, buildFetchParams]);

  const { list: requests, total } = resolvePaginatedList(data);

  const openDetail = useCallback((request) => {
    setDetailRequest(request);
    setDetailOpen(true);
  }, []);

  const closeDetail = () => {
    setDetailOpen(false);
    setDetailRequest(null);
  };

  const refreshList = useCallback(() => {
    fetchRequests(buildFetchParams());
  }, [fetchRequests, buildFetchParams]);

  const handleApprove = (request) => {
    makeDecision.mutate(
      {
        slug: request.slug,
        payload: { status: LEAVE_STATUS.APPROVED }
      },
      {
        onSuccess: () => {
          toastr.success("Leave request approved");
          closeDetail();
          refreshList();
        }
      }
    );
  };

  const handleReject = (request, { rejectionReason, fitNoteStatus }) => {
    if (!rejectionReason) {
      toastr.error("Please provide a rejection reason.");
      return;
    }

    if (request.fitNote && !fitNoteStatus) {
      toastr.error("Please select a fit note status.");
      return;
    }

    const payload = {
      status: LEAVE_STATUS.REJECTED,
      rejectionReason
    };

    if (request.fitNote && fitNoteStatus) {
      payload.fitNoteStatus = fitNoteStatus;
    }

    makeDecision.mutate(
      { slug: request.slug, payload },
      {
        onSuccess: () => {
          toastr.success("Leave request rejected");
          closeDetail();
          refreshList();
        }
      }
    );
  };

  const handleTypeFilterChange = (value) => {
    setTypeFilter(value);
    if (value === LEAVE_TYPE.GENERAL) {
      setSicknessMarkTypeFilter(ADMIN_LEAVE_FILTER_ALL);
    }
    setPage(1);
  };

  const handleReset = () => {
    setSearch("");
    setSearchInput("");
    setTypeFilter(ADMIN_LEAVE_FILTER_ALL);
    setStatusFilter(ADMIN_LEAVE_FILTER_ALL);
    setSicknessMarkTypeFilter(ADMIN_LEAVE_FILTER_ALL);
    setLimit(10);
    setPage(1);
  };

  const columns = useMemo(
    () => [
      {
        header: "Worker",
        accessorKey: "worker.user.fullName",
        enableColumnFilter: false,
        cell: (info) => (
          <div>
            <div className="fw-bold">{info.getValue() || "-"}</div>
            <small className="text-muted">{info.row.original.worker?.user?.email}</small>
          </div>
        )
      },
      {
        header: "Type",
        accessorKey: "type",
        enableColumnFilter: false,
        cell: (info) => LEAVE_TYPE_LABELS[info.getValue()] || info.getValue() || "-"
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (info) => {
          const status = info.getValue();
          let color = "secondary";
          if (status === LEAVE_STATUS.APPROVED) color = "success";
          if (status === LEAVE_STATUS.REJECTED) color = "danger";
          if (status === LEAVE_STATUS.PENDING) color = "warning";

          return (
            <Badge color={color} className="font-size-12">
              {LEAVE_STATUS_LABELS[status] || status}
            </Badge>
          );
        }
      },
      {
        header: "View",
        id: "view",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (info) => (
          <Button color="primary" size="sm" outline onClick={() => openDetail(info.row.original)}>
            <i className="mdi mdi-eye me-1" />
            View
          </Button>
        )
      }
    ],
    [openDetail]
  );

  const showSicknessMarkFilter =
    typeFilter === ADMIN_LEAVE_FILTER_ALL || typeFilter === LEAVE_TYPE.SICK;

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Admin" breadcrumbItem="Leave Requests" />

        <Row className="mb-3 g-2 align-items-center">
          <Col md={3}>
            <Input
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>

          <Col md={2}>
            <Input
              type="select"
              value={typeFilter}
              onChange={(e) => handleTypeFilterChange(e.target.value)}
            >
              <option value={ADMIN_LEAVE_FILTER_ALL}>All types</option>
              <option value={LEAVE_TYPE.SICK}>{LEAVE_TYPE_LABELS[LEAVE_TYPE.SICK]}</option>
              <option value={LEAVE_TYPE.GENERAL}>
                {LEAVE_TYPE_LABELS[LEAVE_TYPE.GENERAL]}
              </option>
            </Input>
          </Col>

          <Col md={2}>
            <Input
              type="select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value={ADMIN_LEAVE_FILTER_ALL}>All statuses</option>
              <option value={LEAVE_STATUS.PENDING}>
                {LEAVE_STATUS_LABELS[LEAVE_STATUS.PENDING]}
              </option>
              <option value={LEAVE_STATUS.APPROVED}>
                {LEAVE_STATUS_LABELS[LEAVE_STATUS.APPROVED]}
              </option>
              <option value={LEAVE_STATUS.REJECTED}>
                {LEAVE_STATUS_LABELS[LEAVE_STATUS.REJECTED]}
              </option>
            </Input>
          </Col>

          {showSicknessMarkFilter && (
            <Col md={2}>
              <Input
                type="select"
                value={sicknessMarkTypeFilter}
                onChange={(e) => {
                  setSicknessMarkTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value={ADMIN_LEAVE_FILTER_ALL}>All sick marks</option>
                <option value={SICKNESS_MARK_TYPE.NORMAL}>
                  {SICKNESS_MARK_TYPE_LABELS[SICKNESS_MARK_TYPE.NORMAL]}
                </option>
                <option value={SICKNESS_MARK_TYPE.LATE}>
                  {SICKNESS_MARK_TYPE_LABELS[SICKNESS_MARK_TYPE.LATE]}
                </option>
              </Input>
            </Col>
          )}

          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center flex-wrap">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={isLoading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button color="secondary" outline onClick={handleReset} disabled={isLoading}>
              Reset
            </Button>
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button color="light" onClick={refreshList} disabled={isLoading}>
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Row>
          <Col xs="12">
            <Card>
              <CardBody>
                {isLoading && requests.length === 0 ? (
                  <Spinners setLoading={() => {}} />
                ) : error ? (
                  <div className="text-danger">Failed to load leave requests.</div>
                ) : (
                  <>
                    <TableContainer
                      columns={columns}
                      data={requests}
                      isGlobalFilter={false}
                      isPagination={false}
                      isCustomPageSize={false}
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                      theadClass="table-light"
                      divClassName="table-responsive"
                    />
                    <Paginations
                      total={total}
                      perPageData={limit}
                      currentPage={page}
                      setCurrentPage={setPage}
                      isShowingPageLength
                      paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                      paginationClass="pagination"
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <LeaveRequestDetailModal
        isOpen={detailOpen}
        toggle={closeDetail}
        request={detailRequest}
        onApprove={handleApprove}
        onReject={handleReject}
        isSubmitting={makeDecision.isPending}
      />
    </div>
  );
};

export default LeaveRequests;
